/**
 * feAI – Frontend
 * src/pages/SsoAuthPage.tsx
 *
 * Pagina dedicata all'auto-login via SSO dal server legacy.
 *
 * Legge i parametri sso_token, sso_cod_agente, sso_username, sso_ts dall'URL,
 * chiama l'endpoint /api/v1/core/auth/sso-login e:
 *   - SUCCESSO → redirect a /dashboard
 *   - FALLIMENTO → redirect a /login con messaggio di errore
 *   - GIÀ AUTENTICATO → redirect a /dashboard
 *
 * Mostra uno spinner "Verifica accesso in corso…" durante l'elaborazione.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../hooks/useAuth';

export const SsoAuthPage: React.FC = () => {
  const { step, ssoLogin, error } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const [attempted, setAttempted] = useState(false);

  // ── Legge i parametri SSO dall'URL e avvia il login ──────────────────────
  useEffect(() => {
    const ssoToken = searchParams.get('sso_token');
    const ssoCodAgente = searchParams.get('sso_cod_agente');
    const ssoUsername = searchParams.get('sso_username');
    const ssoTs = searchParams.get('sso_ts');

    // Se mancano parametri → reindirizza al login normale
    if (!ssoToken || !ssoCodAgente || !ssoUsername || !ssoTs) {
      navigate('/login', { replace: true });
      return;
    }

    const codAgente = parseInt(ssoCodAgente, 10);
    const ts = parseInt(ssoTs, 10);

    if (isNaN(codAgente) || isNaN(ts)) {
      navigate('/login', { replace: true });
      return;
    }

    // Pulisce l'URL dai parametri SSO (per evitare refresh accidentali)
    window.history.replaceState({}, document.title, '/sso-auth');

    // Pulisce COMPLETAMENTE la cache React Query del vecchio utente.
    // Senza questo, i dati del vecchio utente (dashboard, ecc.) rimangono in
    // cache e vengono mostrati anche dopo il cambio utente via SSO, causando
    // il bug per cui "in basso scrive il secondo utente ma nella dashboard
    // rimane il precedente".
    queryClient.clear();

    setAttempted(true);
    ssoLogin(ssoToken, codAgente, ssoUsername, ts);
  }, [searchParams, ssoLogin, navigate, queryClient]);

  // ── Reazione al risultato del login SSO ─────────────────────────────────
  useEffect(() => {
    if (step.type === 'authenticated') {
      navigate('/dashboard', { replace: true });
      return;
    }

    // Se SSO fallito (step torna a idle dopo tentativo) → redirect a /login
    // passando il messaggio di errore via query string, perché LoginPage ha
    // una sua istanza indipendente di useAuth e non vedrebbe l'errore locale.
    if (attempted && step.type === 'idle') {
      const params = new URLSearchParams();
      if (error) params.set('sso_error', error);
      else params.set('sso_error', 'Accesso automatico non riuscito, riprova o accedi manualmente.');
      navigate(`/login?${params.toString()}`, { replace: true });
    }
  }, [step, attempted, navigate, error]);

  return (
    <main
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: 'var(--bg-primary, #0f0f13)',
        color: 'var(--text-secondary, #888)',
        fontFamily: 'system-ui, sans-serif',
      }}
      aria-label="Verifica accesso SSO in corso"
    >
      <div style={{ textAlign: 'center' }}>
        <div
          className="spinner"
          style={{
            width: 40,
            height: 40,
            margin: '0 auto 1rem',
            border: '3px solid var(--border-subtle, #2a2a2e)',
            borderTopColor: 'var(--brand-primary, #e50914)',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }}
          aria-hidden="true"
        />
        <p>Verifica accesso in corso…</p>
      </div>

      {/* Keyframe per lo spinner inline */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </main>
  );
};

export default SsoAuthPage;